import { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Loader2, Network, ImagePlus, CornerDownRight, FilePlus2 } from 'lucide-react';
import { toast } from 'sonner';
import { thesisApi } from '@/api/thesisApi';

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

export const CodeFigureCard = ({ userId, codeProject, language, chapters = [], onInserted }) => {
  const [busy, setBusy] = useState(false);
  const [figure, setFigure] = useState(null);
  const [caption, setCaption] = useState('');
  const [target, setTarget] = useState('');
  const [inserting, setInserting] = useState(false);

  const generate = async () => {
    setBusy(true); setFigure(null);
    try {
      const { job_id } = await thesisApi.startCodeFigure(userId, codeProject.id, language);
      for (let i = 0; i < 120; i += 1) {
        await wait(4000);
        const job = await thesisApi.getCodeJob(userId, codeProject.id, job_id);
        if (job.status === 'done') { setFigure(job.result); setCaption(job.result.caption || ''); break; }
        if (job.status === 'error') throw new Error(job.error || 'Diagram generation failed');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || 'Diagram generation failed');
    } finally {
      setBusy(false);
    }
  };

  const insert = async () => {
    setInserting(true);
    try {
      const res = await thesisApi.insertCodeFigure(userId, codeProject.id, {
        figureId: figure.figure_id, caption, chapterId: target || null,
      });
      toast.success(target ? 'Figure appended to the selected section' : 'Figure added as a new section');
      onInserted?.(res.chapter_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not insert the figure');
    } finally {
      setInserting(false);
    }
  };

  return (
    <Card data-testid="code-figure-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base md:text-lg">
          <Network className="w-4 h-4" />Architecture figure from the code
        </CardTitle>
        <CardDescription>
          The pipeline is derived only from the verified claims — inputs, computation and outputs —
          and rendered as a publication-ready diagram with a caption naming the code version.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 min-w-0 max-w-full">
        <Button onClick={generate} disabled={busy} data-testid="code-figure-generate-btn">
          {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Drawing the pipeline (1–2 min)…</>
            : <><ImagePlus className="w-4 h-4 mr-2" />{figure ? 'Redraw diagram' : 'Generate diagram'}</>}
        </Button>

        {figure && (
          <div className="space-y-3">
            <div className="rounded-lg border border-border bg-white p-2 overflow-auto max-w-full min-w-0">
              <img src={figure.image_url} alt={caption || 'Architecture diagram'}
                className="max-w-full h-auto" data-testid="code-figure-image" />
            </div>
            <div className="flex flex-wrap gap-2">
              {(figure.nodes || []).slice(0, 8).map((n, i) => (
                <Badge key={i} variant="outline" className="text-[10px]">
                  {n.label}{(n.markers || []).length ? ` · ${n.markers.join(' ')}` : ''}
                </Badge>
              ))}
            </div>
            <Input value={caption} onChange={(e) => setCaption(e.target.value)}
              placeholder="Figure caption" data-testid="code-figure-caption-input" />
            <div className="flex flex-wrap gap-2 items-center">
              <select className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                value={target} onChange={(e) => setTarget(e.target.value)}
                data-testid="code-figure-target-select">
                <option value="">— create a new section —</option>
                {chapters.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
              </select>
              <Button onClick={insert} disabled={inserting} data-testid="code-figure-insert-btn">
                {inserting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  : target ? <CornerDownRight className="w-4 h-4 mr-2" /> : <FilePlus2 className="w-4 h-4 mr-2" />}
                {target ? 'Append figure' : 'Insert as new section'}
              </Button>
            </div>
            <details className="text-xs text-muted-foreground">
              <summary className="cursor-pointer">Mermaid source (editable in any diagram tool)</summary>
              <pre className="mt-2 whitespace-pre-wrap break-words border border-border rounded p-2 bg-muted/30"
                data-testid="code-figure-mermaid">{figure.mermaid}</pre>
            </details>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CodeFigureCard;
